import { useState, useEffect } from "react";
import { questions as localQuestions, calculateResults, koshaInfo as localKoshaInfo } from "@/lib/quizData";
import { ChevronRight, ChevronLeft, Loader2, ChevronDown } from "lucide-react";
import LotusIcon from "./icons/LotusIcon";
import siwonLogo from "@/assets/siwon-logo.png";
import { submitQuizResults, submitLeadCapture } from "@/services/googleFormService";
import { fetchQuizDataFromSheet } from "@/services/googleSheetService";
import { countryCodes } from "@/data/countryCodes";

export const DiagnosticQuiz = ({ onComplete }) => {
  const [step, setStep] = useState('intro');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [userData, setUserData] = useState({ name: '', email: '', phone: '', countryCode: '+91' });
  const [errors, setErrors] = useState({});
  const [slideDirection, setSlideDirection] = useState('right');
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Dynamic data from Google Sheets
  const [questions, setQuestions] = useState(localQuestions);
  const [koshaInfo, setKoshaInfo] = useState(localKoshaInfo);
  const [dataSource, setDataSource] = useState('local');
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Load quiz data from Google Sheets on mount
  useEffect(() => {
    const loadQuizData = async () => {
      try {
        setIsLoadingData(true);
        const sheetData = await fetchQuizDataFromSheet();
        
        if (sheetData.success && sheetData.relationshipQuestions.length > 0) {
          setQuestions(sheetData.relationshipQuestions);
          if (Object.keys(sheetData.relationshipKoshaInfo).length > 0) {
            setKoshaInfo(sheetData.relationshipKoshaInfo);
          }
          setDataSource('sheet');
          console.log('Loaded quiz data from Google Sheets');
        } else {
          console.log('Using local quiz data (fallback)');
          setDataSource('local');
        }
      } catch (error) {
        console.error('Error loading quiz data:', error);
        setDataSource('local');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadQuizData();
  }, []);

  const question = questions[currentQuestion];
  const progress = questions.length > 0 ? ((currentQuestion + 1) / questions.length) * 100 : 0;
  const kosha = question ? koshaInfo[question.kosha] : null;

  const validateIntro = () => {
    const newErrors = {};
    
    if (!userData.name.trim()) {
      newErrors.name = "Please enter your name";
    }
    
    if (!userData.email.trim()) {
      newErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!userData.phone.trim()) {
      newErrors.phone = "Please enter your mobile number";
    } else if (!/^\d{10}$/.test(userData.phone.replace(/\s/g, ''))) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Get full phone number with country code
  const getFullPhoneNumber = () => {
    return `${userData.countryCode}${userData.phone.replace(/\s/g, '')}`;
  };

  const handleStartQuiz = async () => {
    if (validateIntro()) {
      // Submit lead capture BEFORE starting quiz with full phone number
      try {
        const leadData = {
          ...userData,
          phone: getFullPhoneNumber()
        };
        await submitLeadCapture(leadData, "Relationship");
        console.log('Lead captured successfully');
      } catch (error) {
        console.error('Lead capture error:', error);
      }
      
      setStep('quiz');
    }
  };

  const handleAnswer = (value) => {
    if (question) {
      setAnswers(prev => ({ ...prev, [question.id]: value }));
    }
  };

  const handleNext = () => {
    if (isAnimating || !question) return;
    
    if (currentQuestion < questions.length - 1) {
      setSlideDirection('right');
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
        setIsAnimating(false);
      }, 300);
    } else {
      setStep('loading');
      const results = calculateResults(answers);
      
      // Create user data with full phone number for storage and submission
      const userDataWithFullPhone = {
        ...userData,
        phone: getFullPhoneNumber()
      };
      
      localStorage.setItem('siwon_quiz_results', JSON.stringify(results));
      localStorage.setItem('siwon_user_data', JSON.stringify(userDataWithFullPhone));
      
      // Submit complete results to Google Form with full phone number
      submitQuizResults(userDataWithFullPhone, results, "Relationship");
      
      setTimeout(() => {
        onComplete(results, userDataWithFullPhone);
      }, 2000);
    }
  };

  const handlePrev = () => {
    if (isAnimating || currentQuestion === 0) return;
    
    setSlideDirection('left');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentQuestion(prev => prev - 1);
      setIsAnimating(false);
    }, 300);
  };

  if (step === 'loading') {
    return (
      <section id="quiz" className="py-20 md:py-28 bg-primary mandala-bg">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <img src={siwonLogo} alt="Siwon Galaxy" className="h-20 mx-auto mb-8 brightness-0 invert animate-float" />
            <h2 className="font-serif text-3xl md:text-4xl text-primary-foreground mb-4">
              Analyzing Your Koshas...
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Preparing your personalized relationship diagnostic report
            </p>
            <Loader2 className="w-12 h-12 text-accent mx-auto animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  if (step === 'intro') {
    return (
      <section id="quiz" className="py-20 md:py-28 bg-sage-dark mandala-bg">
        <div className="container">
          <div className="max-w-xl mx-auto">
            <div className="card-spiritual text-center animate-scale-in">
              <LotusIcon size={60} className="mx-auto mb-6" />
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-primary mb-4">
                Begin Your Journey
              </h2>
              <p className="text-muted-foreground mb-8">
                Enter your details to receive your personalized relationship diagnostic report
              </p>

              {isLoadingData && (
                <div className="mb-4 text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading quiz data...
                </div>
              )}

              <div className="space-y-4 text-left mb-8">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={userData.name}
                    onChange={(e) => setUserData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter your name"
                    className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-destructive' : 'border-border'} bg-background focus:outline-none focus:ring-2 focus:ring-accent transition-all`}
                    data-testid="quiz-name-input"
                  />
                  {errors.name && <p className="text-sm text-destructive mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter your email"
                    className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-destructive' : 'border-border'} bg-background focus:outline-none focus:ring-2 focus:ring-accent transition-all`}
                    data-testid="quiz-email-input"
                  />
                  {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Mobile Number
                  </label>
                  <div className="flex gap-2">
                    {/* Country Code Dropdown */}
                    <div className="relative">
                      <select
                        value={userData.countryCode}
                        onChange={(e) => setUserData(prev => ({ ...prev, countryCode: e.target.value }))}
                        className="appearance-none w-28 px-3 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent transition-all cursor-pointer pr-8 font-medium"
                        data-testid="quiz-country-code"
                      >
                        {countryCodes.map((cc) => (
                          <option key={cc.code} value={cc.code}>
                            {cc.flag} {cc.code}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    </div>
                    {/* Phone Number Input */}
                    <input
                      type="tel"
                      value={userData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setUserData(prev => ({ ...prev, phone: value }));
                      }}
                      placeholder="10-digit number"
                      maxLength={10}
                      className={`flex-1 px-4 py-3 rounded-lg border ${errors.phone ? 'border-destructive' : 'border-border'} bg-background focus:outline-none focus:ring-2 focus:ring-accent transition-all`}
                      data-testid="quiz-phone-input"
                    />
                  </div>
                  {errors.phone && <p className="text-sm text-destructive mt-1">{errors.phone}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Select country code and enter 10-digit number</p>
                </div>
              </div>

              <button
                onClick={handleStartQuiz}
                disabled={isLoadingData}
                className="btn-lime w-full text-lg disabled:opacity-50"
                data-testid="quiz-start-btn"
              >
                Start the Diagnostic Quiz
                <ChevronRight className="inline-block ml-2 w-5 h-5" />
              </button>

              <p className="text-xs text-muted-foreground mt-4">
                Your information is kept confidential and will only be used to send your results.
              </p>
              
              {dataSource === 'sheet' && (
                <p className="text-xs text-accent mt-2">
                  ✓ Dynamic quiz loaded from Google Sheets
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="quiz" className="py-20 md:py-28 bg-sage-dark mandala-bg min-h-screen">
      <div className="container">
        <div className="max-w-2xl mx-auto">
          {/* Kosha Section Progress */}
          {kosha && (
            <div className="mb-4">
              <div className="flex items-center justify-center gap-3 mb-3">
                <span className="text-2xl">{kosha.icon}</span>
                <div className="text-center">
                  <p className="text-sm font-medium text-accent">
                    Section {['annamaya', 'pranamaya', 'manomaya', 'vijnanamaya', 'anandamaya'].indexOf(question.kosha) + 1} of 5
                  </p>
                  <p className="font-serif text-lg font-semibold text-primary">{kosha.name}</p>
                  <p className="text-xs text-muted-foreground">{kosha.subtitle}</p>
                </div>
              </div>
              {/* Section dots */}
              <div className="flex justify-center gap-2 mb-2">
                {['annamaya', 'pranamaya', 'manomaya', 'vijnanamaya', 'anandamaya'].map((k, idx) => (
                  <div
                    key={k}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      k === question.kosha 
                        ? 'bg-accent scale-125' 
                        : idx < ['annamaya', 'pranamaya', 'manomaya', 'vijnanamaya', 'anandamaya'].indexOf(question.kosha)
                          ? 'bg-primary'
                          : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Question Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-muted-foreground">
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-sm font-medium text-accent">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          {question && (
            <div 
              className={`card-spiritual transition-all duration-300 ${
                isAnimating 
                  ? slideDirection === 'right' 
                    ? 'opacity-0 translate-x-8' 
                    : 'opacity-0 -translate-x-8'
                  : 'opacity-100 translate-x-0'
              }`} 
              key={currentQuestion}
            >
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-primary mb-8 text-center">
                {question.question}
              </h3>

              {/* Options */}
              <div className="space-y-3 mb-8">
                {question.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-300 ${
                      answers[question.id] === option.value
                        ? 'border-accent bg-accent/10 text-primary'
                        : 'border-border hover:border-accent/50 hover:bg-accent/5'
                    }`}
                    data-testid={`quiz-option-${option.value}`}
                  >
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <button
                  onClick={handlePrev}
                  disabled={currentQuestion === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                    currentQuestion === 0
                      ? 'text-muted-foreground cursor-not-allowed'
                      : 'text-primary hover:bg-muted'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </button>

                <button
                  onClick={handleNext}
                  disabled={!answers[question.id]}
                  className={`flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all ${
                    answers[question.id]
                      ? 'btn-lime'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                  data-testid="quiz-next-btn"
                >
                  {currentQuestion === questions.length - 1 ? 'See Results' : 'Next'}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DiagnosticQuiz;
